import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

// RC PATCH (D-056): the latest quick-add click wins. Every card fetches on its own, so without this a slower
// response to an earlier click could re-point a dialog already open on a later one.
let quickAddClickToken = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {{ url: string, promise: Promise<Document | null> } | null} RC PATCH (D-056): see fetchProductPage */
  #inflight = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    const clickToken = ++quickAddClickToken; // RC PATCH (D-056)

    // RC PATCH (D-056): a product with several options opens its product page, a deliberate deviation.
    // Production's grid "+" adds the card's default variant without asking; production's picker rules for
    // several options aren't ported to Horizon's picker (D-052), so the full product page is the safe place to
    // choose them.
    if (this.#opensProductPage) {
      this.#openProductPage(event, currentUrl);
      return;
    }

    // RC PATCH (D-056, D-048c I-A): the morph keeps the dialog's form and picker across opens. Drop a click
    // held from the previous open and abort its pending picker fetch, so a reopen starts clean and a late
    // response can't re-point the reused dialog.
    this.#resetReusedDialog();

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      /** @type {Document | null} */
      let html = null;
      try {
        html = await this.fetchProductPage(currentUrl);
      } catch (error) {
        // RC PATCH (D-056): the view didn't load (an HTTP or network error). Open the product page, not nothing.
        console.error(error);
        if (clickToken === quickAddClickToken) this.#openProductPage(event, currentUrl);
        return;
      }
      // RC PATCH (D-056): a newer click took over, or this card's link changed and aborted the fetch.
      if (!html || clickToken !== quickAddClickToken) return;
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (gridElement) {
        // Cache the cloned element to avoid modifying the original
        productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(currentUrl, productGrid);
      }
    }

    if (!productGrid) {
      // RC PATCH (D-056): no product details to show. Open the product page instead of an empty dialog, or one
      // still holding the previous product (QA DEF-023).
      this.#openProductPage(event, currentUrl);
      return;
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    if (clickToken !== quickAddClickToken) return; // RC PATCH (D-056)
    this.#updateVariantPicker(productGrid);

    this.#openQuickAddModal();
  };

  /**
   * RC PATCH (D-056): go to the product page; Cmd/Ctrl/Shift-click opens it in a new tab, as a link would.
   * @param {Event} event
   * @param {string} url
   */
  #openProductPage(event, url) {
    if (!url) return;
    const click = /** @type {MouseEvent} */ (event);
    if (click.metaKey || click.ctrlKey || click.shiftKey) {
      window.open(url, '_blank', 'noopener');
    } else {
      window.location.href = url;
    }
  }

  /** RC PATCH (D-056): true when the product has more than one option (see handleClick). */
  get #opensProductPage() {
    return Number(this.dataset.productOptionsCount) > 1;
  }

  /** RC PATCH (D-056, D-048c I-A): see handleClick. */
  #resetReusedDialog() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    for (const picker of modalContent.querySelectorAll('variant-picker')) {
      if (picker instanceof VariantPicker) picker.abortPendingFetch();
    }
    for (const form of modalContent.querySelectorAll('product-form-component')) {
      /** @type {HTMLElement & { resetPendingVariantChange?: () => void }} */ (form).resetPendingVariantChange?.();
    }
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    if (this.#opensProductPage) return null; // RC PATCH (D-056): no dialog, so no prefetch either

    // RC PATCH (D-056): a prefetch (hover, focus, a card variant update) and a click for the same URL share one
    // request. Aborting it would leave the click with nothing to show.
    if (this.#inflight?.url === productPageUrl) return this.#inflight.promise;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    // RC PATCH (D-056): most products are assigned legacy templates, which don't render Horizon's product
    // details; this view renders them for any product (templates/product.rc-quick-add.json, QA DEF-023).
    const fetchUrl = new URL(productPageUrl, window.location.origin);
    fetchUrl.searchParams.set('view', 'rc-quick-add');

    /** @type {{ url: string, promise: Promise<Document | null> }} */
    const request = { url: productPageUrl, promise: Promise.resolve(null) };
    this.#inflight = request;
    request.promise = (async () => {
      try {
        const response = await fetch(fetchUrl.toString(), {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
        }

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        return html;
      } catch (error) {
        if (error.name === 'AbortError') {
          return null;
        } else {
          throw error;
        }
      } finally {
        // RC PATCH (D-056): clear only our own request; a newer one may already be running.
        if (this.#abortController === abortController) this.#abortController = null;
        if (this.#inflight === request) this.#inflight = null;
      }
    })();
    return request.promise;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // RC PATCH (D-056): a different product gets fresh elements instead of the previous product's form and picker
    // morphed into it, so a late event from the previous form (an add still in flight) can't reach the new
    // dialog, and the picker's radio arrays belong to the new product.
    const currentProductId = modalContent.querySelector('product-form-component')?.getAttribute('data-product-id');
    const nextProductId = productGrid.querySelector('product-form-component')?.getAttribute('data-product-id');
    if (currentProductId && currentProductId !== nextProductId) modalContent.replaceChildren();

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
